import { datadogRum } from "@datadog/browser-rum";

import type { OptOutReasonKey } from "features/scheduling-v3/domain/settings-document";

/**
 * ENG-1981 adoption telemetry — the single writer of these action names, so a
 * dashboard or a RUM-generated metric has one place to look for what the payload means.
 *
 * Who and which practice are not in the payload: `auth-slice` sets them on RUM's global
 * context after `/me` (see `quick-launcher-telemetry.ts`). Every field is one of a fixed
 * handful of values, so the actions stay safe to turn into grouped metrics. The free-text
 * "anything else?" answer is NEVER sent here: it could name a patient. It is stored in the
 * person's own settings document, and the backend records it from there as feature feedback
 * (ENG-2385). Since ENG-2210 the reason is required, so an opt-out always carries one of
 * the six reason keys ("skipped" is no longer sent).
 */
export const SCHEDULER_OPT_IN_ACTION = "scheduling_v3_opt_in";
export const SCHEDULER_OPT_OUT_ACTION = "scheduling_v3_opt_out";
export const SCHEDULER_OFFER_NOT_NOW_ACTION = "scheduling_v3_offer_not_now";
/**
 * ENG-2384: the new scheduler's guide — from V3's first-visit callout or its band's link, or
 * from classic's offer banner or resting strip (did reading it first lead to a switch?).
 */
export const SCHEDULER_GUIDE_OPENED_ACTION = "scheduling_v3_guide_opened";
export const SCHEDULER_GUIDE_DISMISSED_ACTION = "scheduling_v3_guide_dismissed";

export type SchedulerGuideSource =
  | "welcome"
  | "side_panel"
  | "banner"
  | "classic_strip";

export type SchedulerSwitchSource =
  | "banner"
  | "classic_strip"
  | "account_menu"
  | "side_panel";

const track = (name: string, context: Record<string, string>) => {
  try {
    datadogRum.addAction(name, context);
  } catch {
    // Telemetry must never break switching schedulers.
  }
};

export const trackSchedulerOptIn = (source: SchedulerSwitchSource) =>
  track(SCHEDULER_OPT_IN_ACTION, { source });

export const trackSchedulerOptOut = (
  source: SchedulerSwitchSource,
  reason: OptOutReasonKey
) => track(SCHEDULER_OPT_OUT_ACTION, { source, reason });

export const trackSchedulerOfferNotNow = () =>
  track(SCHEDULER_OFFER_NOT_NOW_ACTION, { source: "banner" });

export const trackSchedulerGuideOpened = (source: SchedulerGuideSource) =>
  track(SCHEDULER_GUIDE_OPENED_ACTION, { source });

export const trackSchedulerGuideDismissed = () =>
  track(SCHEDULER_GUIDE_DISMISSED_ACTION, { source: "welcome" });
